// Comprehensive Supabase connectivity test
import { getSupabaseUrl, getSupabaseAnonKey } from '../config/supabase';

export const runSupabaseConnectivityTest = async () => {
  console.log('🔍 Starting comprehensive Supabase connectivity test...');
  
  const results = {
    configCheck: false,
    basicPing: false,
    authEndpoint: false,
    restEndpoint: false,
    postRequest: false,
    putRequest: false,
    patchRequest: false,
    deleteRequest: false,
    errors: []
  };

  try {
    // 1. Configuration check
    console.log('1️⃣ Testing Supabase configuration...');
    let supabaseUrl, supabaseKey;
    
    try {
      supabaseUrl = getSupabaseUrl();
      supabaseKey = getSupabaseAnonKey();
      
      if (supabaseUrl && supabaseKey) {
        results.configCheck = true;
        console.log('✅ Configuration valid');
        console.log('🔗 URL:', supabaseUrl);
        console.log('🔑 Key:', supabaseKey.substring(0, 20) + '...');
      } else {
        console.log('❌ Configuration invalid');
        results.errors.push('Invalid Supabase configuration');
        return results;
      }
    } catch (error) {
      console.log('❌ Configuration error:', error.message);
      results.errors.push(`Configuration error: ${error.message}`);
      return results;
    }

    // 2. Basic ping test
    console.log('2️⃣ Testing basic connectivity...');
    try {
      const pingResponse = await fetch(supabaseUrl, { 
        method: 'HEAD',
        timeout: 5000 
      });
      results.basicPing = pingResponse.ok;
      console.log(results.basicPing ? '✅ Basic ping successful' : '❌ Basic ping failed');
    } catch (error) {
      console.log('❌ Basic ping failed:', error.message);
      results.errors.push(`Basic ping: ${error.message}`);
    }

    // 3. Auth endpoint test
    console.log('3️⃣ Testing auth endpoint...');
    try {
      const authResponse = await fetch(`${supabaseUrl}/auth/v1/settings`, {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        },
        timeout: 5000
      });
      results.authEndpoint = authResponse.ok;
      console.log(results.authEndpoint ? '✅ Auth endpoint accessible' : '❌ Auth endpoint failed');
    } catch (error) {
      console.log('❌ Auth endpoint failed:', error.message);
      results.errors.push(`Auth endpoint: ${error.message}`);
    }

    // 4. REST endpoint GET test
    console.log('4️⃣ Testing REST endpoint (GET)...');
    try {
      const restResponse = await fetch(`${supabaseUrl}/rest/v1/`, {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        },
        timeout: 5000
      });
      results.restEndpoint = restResponse.ok;
      console.log(results.restEndpoint ? '✅ REST GET successful' : '❌ REST GET failed');
    } catch (error) {
      console.log('❌ REST GET failed:', error.message);
      results.errors.push(`REST GET: ${error.message}`);
    }

    // 5. POST request test (the problematic one)
    console.log('5️⃣ Testing POST request...');
    try {
      const postResponse = await fetch(`${supabaseUrl}/rest/v1/tmdb_cache`, {
        method: 'POST',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          cache_key: 'connectivity-test',
          title: 'Connectivity Test',
          year: 2024,
          tmdb_data: { test: true },
          cached_at: new Date().toISOString()
        }),
        timeout: 5000
      });
      results.postRequest = postResponse.ok || postResponse.status === 409; // 409 is acceptable (conflict)
      if (results.postRequest) {
        console.log('✅ POST request successful');
      } else {
        const errorText = await postResponse.text();
        console.log(`❌ POST request failed: ${postResponse.status} - ${errorText}`);
        results.errors.push(`POST request: ${postResponse.status} - ${errorText}`);
      }
    } catch (error) {
      console.log('❌ POST request failed:', error.message);
      results.errors.push(`POST request: ${error.message}`);
    }

    // 6. PUT request test
    console.log('6️⃣ Testing PUT request...');
    try {
      // First, try to find an existing record to update
      const findResponse = await fetch(`${supabaseUrl}/rest/v1/tmdb_cache?cache_key=eq.connectivity-test&select=id`, {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        }
      });
      
      let putUrl = `${supabaseUrl}/rest/v1/tmdb_cache`;
      if (findResponse.ok) {
        const existingRecords = await findResponse.json();
        if (existingRecords.length > 0) {
          putUrl = `${supabaseUrl}/rest/v1/tmdb_cache?id=eq.${existingRecords[0].id}`;
          console.log('🔍 Found existing record to update:', existingRecords[0].id);
        } else {
          console.log('🔍 No existing record found, PUT will create new record');
        }
      }
      
      const putResponse = await fetch(putUrl, {
        method: 'PUT',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          cache_key: 'connectivity-test',
          title: 'Connectivity Test Updated',
          year: 2024,
          tmdb_data: { test: true, updated: true },
          cached_at: new Date().toISOString()
        }),
        timeout: 5000
      });
      results.putRequest = putResponse.ok;
      if (results.putRequest) {
        console.log('✅ PUT request successful');
      } else {
        const errorText = await putResponse.text();
        console.log(`❌ PUT request failed: ${putResponse.status} - ${errorText}`);
        results.errors.push(`PUT request: ${putResponse.status} - ${errorText}`);
      }
    } catch (error) {
      console.log('❌ PUT request failed:', error.message);
      results.errors.push(`PUT request: ${error.message}`);
    }

    // 7. PATCH request test
    console.log('7️⃣ Testing PATCH request...');
    try {
      const patchResponse = await fetch(`${supabaseUrl}/rest/v1/tmdb_cache?cache_key=eq.connectivity-test`, {
        method: 'PATCH',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          tmdb_data: { test: true, patched: true }
        }),
        timeout: 5000
      });
      results.patchRequest = patchResponse.ok;
      if (results.patchRequest) {
        console.log('✅ PATCH request successful');
      } else {
        const errorText = await patchResponse.text();
        console.log(`❌ PATCH request failed: ${patchResponse.status} - ${errorText}`);
        results.errors.push(`PATCH request: ${patchResponse.status} - ${errorText}`);
      }
    } catch (error) {
      console.log('❌ PATCH request failed:', error.message);
      results.errors.push(`PATCH request: ${error.message}`);
    }

    // 8. DELETE request test (cleanup)
    console.log('8️⃣ Testing DELETE request (cleanup)...');
    try {
      const deleteResponse = await fetch(`${supabaseUrl}/rest/v1/tmdb_cache?cache_key=eq.connectivity-test`, {
        method: 'DELETE',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        },
        timeout: 5000
      });
      results.deleteRequest = deleteResponse.ok;
      console.log(results.deleteRequest ? '✅ DELETE request successful' : `❌ DELETE request failed: ${deleteResponse.status}`);
    } catch (error) {
      console.log('❌ DELETE request failed:', error.message);
      results.errors.push(`DELETE request: ${error.message}`);
    }

  } catch (error) {
    console.error('❌ Connectivity test failed:', error);
    results.errors.push(`General error: ${error.message}`);
  }

  // Summary
  console.log('\n📊 CONNECTIVITY TEST SUMMARY:');
  console.log('================================');
  console.log(`Config Check: ${results.configCheck ? '✅' : '❌'}`);
  console.log(`Basic Ping: ${results.basicPing ? '✅' : '❌'}`);
  console.log(`Auth Endpoint: ${results.authEndpoint ? '✅' : '❌'}`);
  console.log(`REST GET: ${results.restEndpoint ? '✅' : '❌'}`);
  console.log(`POST Request: ${results.postRequest ? '✅' : '❌'}`);
  console.log(`PUT Request: ${results.putRequest ? '✅' : '❌'}`);
  console.log(`PATCH Request: ${results.patchRequest ? '✅' : '❌'}`);
  console.log(`DELETE Request: ${results.deleteRequest ? '✅' : '❌'}`);
  
  if (results.errors.length > 0) {
    console.log('\n❌ ERRORS ENCOUNTERED:');
    results.errors.forEach((error, index) => {
      console.log(`${index + 1}. ${error}`);
    });
  }

  // Diagnosis
  const writeMethodsWorking = results.postRequest && results.putRequest && results.patchRequest;
  const readMethodsWorking = results.basicPing && results.authEndpoint && results.restEndpoint;
  
  if (readMethodsWorking && !writeMethodsWorking) {
    console.log('\n🚨 DIAGNOSIS: Write operations (POST/PUT/PATCH) are failing while read operations work.');
    console.log('This indicates a Supabase infrastructure issue similar to what we encountered before.');
    console.log('RECOMMENDATION: Migrate to a new Supabase project.');
  } else if (!readMethodsWorking && !writeMethodsWorking) {
    console.log('\n🚨 DIAGNOSIS: Complete connectivity failure.');
    console.log('This could be a network issue or Supabase project is completely down.');
  } else if (writeMethodsWorking && readMethodsWorking) {
    console.log('\n✅ DIAGNOSIS: All connectivity tests passed.');
    console.log('The issue may be elsewhere in the application logic.');
  }

  return results;
};
